/** Shared page geometry for all formal document prints. */

export type PageSizeName = 'letter' | 'a4';

export type PrintTemplateId =
  | 'letter-letterhead'
  | 'a4-portrait'
  | 'a4-landscape'
  | 'a4-report'
  | 'a4-report-landscape';

export type PageTemplate = {
  id: PrintTemplateId;
  pageSize: PageSizeName;
  orientation: 'portrait' | 'landscape';
  pageWidthIn: number;
  pageHeightIn: number;
  marginLeftMm: number;
  marginRightMm: number;
  contentPadTopMm: number;
  contentPadBottomMm: number;
  headerHeightIn: number;
  /** Letterhead footer artwork band (max-height of footer image). */
  footerHeightIn: number;
  /**
   * Extra bottom clearance reserved under the footer artwork so content does not
   * sit under the print safe inset (printer non-printable margin / CSS margin-bottom).
   */
  printSafeInsetBottomIn?: number;
  firstPageHeaderVariant: string;
  continuationHeaderVariant: string;
  footerVariant: string;
};

/** Letter + Quotation/DN/PO letterhead assets. */
export const LETTER_LETTERHEAD_TEMPLATE: PageTemplate = {
  id: 'letter-letterhead',
  pageSize: 'letter',
  orientation: 'portrait',
  pageWidthIn: 8.5,
  pageHeightIn: 11,
  marginLeftMm: 10,
  marginRightMm: 10,
  contentPadTopMm: 2,
  /** Keep totals / amount-words clear of footer grey bar. */
  contentPadBottomMm: 6,
  headerHeightIn: 1.25,
  footerHeightIn: 1.15,
  /** Matches CSS `margin-bottom` on letterhead footer (print + preview). */
  printSafeInsetBottomIn: 0.12,
  firstPageHeaderVariant: 'qtn-header',
  continuationHeaderVariant: 'continuation',
  footerVariant: 'qtn-footer',
};

/** @deprecated Use LETTER_LETTERHEAD_TEMPLATE */
export const QUOTATION_LETTER_TEMPLATE = LETTER_LETTERHEAD_TEMPLATE;

export const A4_PORTRAIT_TEMPLATE: PageTemplate = {
  id: 'a4-portrait',
  pageSize: 'a4',
  orientation: 'portrait',
  pageWidthIn: 210 / 25.4,
  pageHeightIn: 297 / 25.4,
  marginLeftMm: 12,
  marginRightMm: 12,
  contentPadTopMm: 4,
  contentPadBottomMm: 4,
  headerHeightIn: 0.55,
  footerHeightIn: 0.4,
  firstPageHeaderVariant: 'text-header',
  continuationHeaderVariant: 'continuation',
  footerVariant: 'page-number',
};

export const A4_LANDSCAPE_TEMPLATE: PageTemplate = {
  id: 'a4-landscape',
  pageSize: 'a4',
  orientation: 'landscape',
  pageWidthIn: 297 / 25.4,
  pageHeightIn: 210 / 25.4,
  marginLeftMm: 10,
  marginRightMm: 10,
  contentPadTopMm: 3,
  contentPadBottomMm: 3,
  headerHeightIn: 0.45,
  footerHeightIn: 0.35,
  firstPageHeaderVariant: 'text-header',
  continuationHeaderVariant: 'continuation',
  footerVariant: 'page-number',
};

/** Report profile — thinner chrome, denser tables. */
export const A4_REPORT_TEMPLATE: PageTemplate = {
  ...A4_PORTRAIT_TEMPLATE,
  id: 'a4-report',
  headerHeightIn: 0.4,
  footerHeightIn: 0.35,
  contentPadTopMm: 3,
  contentPadBottomMm: 3,
};

/** Report profile on A4 landscape — wide registers (e.g. Tax Invoice Register detail). */
export const A4_REPORT_LANDSCAPE_TEMPLATE: PageTemplate = {
  ...A4_LANDSCAPE_TEMPLATE,
  id: 'a4-report-landscape',
  headerHeightIn: 0.4,
  footerHeightIn: 0.35,
  contentPadTopMm: 3,
  contentPadBottomMm: 3,
};

export const PRINT_TEMPLATES: Record<PrintTemplateId, PageTemplate> = {
  'letter-letterhead': LETTER_LETTERHEAD_TEMPLATE,
  'a4-portrait': A4_PORTRAIT_TEMPLATE,
  'a4-landscape': A4_LANDSCAPE_TEMPLATE,
  'a4-report': A4_REPORT_TEMPLATE,
  'a4-report-landscape': A4_REPORT_LANDSCAPE_TEMPLATE,
};

export function isLandscapePrintTemplate(template: PageTemplate | PrintTemplateId): boolean {
  const t = typeof template === 'string' ? PRINT_TEMPLATES[template] : template;
  return t?.orientation === 'landscape';
}

export function resolvePrintTemplate(id: PrintTemplateId | PageTemplate): PageTemplate {
  return typeof id === 'string' ? PRINT_TEMPLATES[id] : id;
}

const MM_PER_IN = 25.4;

export function inToPx(inches: number, dpi = 96): number {
  return inches * dpi;
}

export function mmToPx(mm: number, dpi = 96): number {
  return (mm / MM_PER_IN) * dpi;
}

export function pxToIn(px: number, dpi = 96): number {
  return px / dpi;
}

export function getContentWidthPx(template: PageTemplate = LETTER_LETTERHEAD_TEMPLATE, dpi = 96): number {
  return inToPx(template.pageWidthIn, dpi) - mmToPx(template.marginLeftMm + template.marginRightMm, dpi);
}

/** Footer artwork + optional print safe inset (must match letterhead CSS). */
export function getFooterBandHeightIn(template: PageTemplate): number {
  return template.footerHeightIn + (template.printSafeInsetBottomIn ?? 0);
}

export function getUsableContentHeightPx(
  template: PageTemplate = LETTER_LETTERHEAD_TEMPLATE,
  dpi = 96,
  opts?: { /** When false, no blank header/footer band (non-letterhead docs). Default true. */ reserveChromeBands?: boolean }
): number {
  const page = inToPx(template.pageHeightIn, dpi);
  const reserve = opts?.reserveChromeBands !== false;
  const header = reserve ? inToPx(template.headerHeightIn, dpi) : 0;
  const footer = reserve ? inToPx(getFooterBandHeightIn(template), dpi) : 0;
  const pad = mmToPx(template.contentPadTopMm + template.contentPadBottomMm, dpi);
  return Math.max(0, page - header - footer - pad);
}

/**
 * Letter stationery always keeps header/footer bands:
 * - with digital letterhead images, or
 * - blank bands for preprinted physical letterhead paper.
 * A4 commercial templates reserve bands only when letterhead artwork is present.
 */
export function shouldReserveStationeryBands(model: {
  templateId: PrintTemplateId;
  letterhead?: { headerSrc: string; footerSrc: string } | null;
}): boolean {
  return !!model.letterhead || model.templateId === 'letter-letterhead';
}

export const CONTINUATION_CHROME_HEIGHT_PX = 28;
export const TABLE_HEADER_HEIGHT_PX = 28;
export const MIN_LINES_TO_START_ROW = 2;
export const LINE_HEIGHT_PX = 16;

export const DEFAULT_LETTERHEAD = {
  headerSrc: '/quotation-letterhead/qtn-header.png',
  footerSrc: '/quotation-letterhead/qtn-footer.jpg',
} as const;
